import type { Offer } from '~/api/pricing-offers'
import type { PaymentProfileSummary } from '~/composables/api/usePayment'
import { ApiV2OffersPostRequestPaymentTypeEnum } from '~/api/pricing-offers'
import { useTranslations } from '~/composables/translate/translations'
import { useOfferStore } from '~/stores/offer'

// TODO: use `offer` store? Why do we need `offer-utils` composable if we already have `offer` store?

export function formattedOfferPricePerDay(offer: Offer, language: string): string | undefined {
  return formatAmountPerDay(language, offer, offer.price)
}

export function formattedOfferPrice(offer: Offer, language: string = useTranslations().language.value): string | undefined {
  return formatAmount(language, offer.price, offer.currency)
}

// Single source for the booking total, so the sticky bar, the footer and the cost
// breakdown can never show different numbers. Extras price comes from the caller
// (`useExtrasStore().totalExtraPrice`) to avoid an import cycle with the stores.
export function formattedBookingTotal(extrasPrice: number, language: string = useTranslations().language.value): string | undefined {
  const offer = useOfferStore().selectedOffer
  return formatAmount(language, (offer?.price ?? 0) + extrasPrice, offer?.currency)
}

export function formatAmount(language: string, amount?: number, currency: string = 'EUR', minimumFractionDigits?: number): string | undefined {
  if (amount === undefined)
    return undefined

  const value = Number(amount)
  const fractionDigits = minimumFractionDigits === 0
    ? (Number.isInteger(value) ? 0 : 2)
    : (minimumFractionDigits ?? 2)

  return value.toLocaleString(
    language,
    { style: 'currency', currency, minimumFractionDigits: fractionDigits, maximumFractionDigits: 2 },
  )
}

export function formatAmountPerDay(language: string, offer?: Offer, amount?: number, needDeviceByDuration: boolean = true): string | undefined {
  if (!offer || !amount)
    return undefined
  const duration = getOfferDaysDuration(offer)
  if (!duration)
    return undefined

  const priceToFormat = needDeviceByDuration ? amount / duration : amount
  return formatAmount(language, priceToFormat, offer.currency)
}

// Rental length as shown next to the total, e.g. "2 days".
export function offerDaysLabel(): string {
  const duration: number = getOfferDaysDuration() ?? 0
  return (duration === 1 ? '% day' : '% days').replace('%', String(duration))
}

export function getOfferDaysDuration(offer: Offer | undefined = useOfferStore().selectedOffer): number | undefined {
  if (offer === undefined) {
    return undefined
  }
  const durationMinutes = offer.properties
    ?.find(property => property.tags?.includes('package_duration_minutes'))
    ?.value

  return durationMinutes
    ? Math.trunc(Number(durationMinutes) / 1440) || 1
    : undefined
}

export function mapPaymentType(paymentType?: string): ApiV2OffersPostRequestPaymentTypeEnum | undefined {
  if (!paymentType)
    return undefined

  const normalized = paymentType.toUpperCase()
  if (['CREDITCARD', 'VISA', 'MASTERCARD', 'MASTER', 'AMEX', 'DISCOVER', 'DINERS_CLUB', 'APPLE_PAY', 'GOOGLE_PAY', 'MOBILE_PAY'].includes(normalized)) {
    return ApiV2OffersPostRequestPaymentTypeEnum.Creditcard
  }
  if (['DIRECTDEBIT', 'STRIPE_SEPA', 'SEPA', 'SOFORT', 'GIROPAY', 'IDEAL', 'EPS'].includes(normalized)) {
    return ApiV2OffersPostRequestPaymentTypeEnum.Directdebit
  }
  if (normalized === 'PAYPAL') {
    return ApiV2OffersPostRequestPaymentTypeEnum.Paypal
  }
  if (normalized === 'INVOICE') {
    return ApiV2OffersPostRequestPaymentTypeEnum.Invoice
  }
  if (normalized === 'WITHDRAWAL') {
    return ApiV2OffersPostRequestPaymentTypeEnum.Withdrawal
  }
  if (normalized === 'PARTNER') {
    return ApiV2OffersPostRequestPaymentTypeEnum.Partner
  }

  return undefined
}

export function resolveAccountType(profile?: PaymentProfileSummary | null): string | undefined {
  if (!profile || profile.ownProfile === undefined)
    return undefined

  return profile.ownProfile ? 'CUST' : 'ALIEN'
}
